'use client';

import { useCallback, useMemo, useState } from 'react';
import {
  isDeepActiveProbeTrace,
  toasterApi,
  useTraceAgentLabel,
} from '@terra/ui-design-system';
import type { SmokeTestHistoryResponseDto } from '@terra/shared-contracts-smoke-test-history';
import {
  abortTraceExecution,
  resumeTraceExecution,
  type TraceActionTarget,
} from '../../hooks/trace-agent-actions';
import { getAuthHeaders } from '../../utils/authHeaders';
import { getCurrentGatewayUrl } from '../../utils/environmentDetection';

const NEO_ABORT_BLOCKING_STATUSES = new Set([
  'COMPLETED',
  'FAILED',
  'ABORTED',
  'PAUSED',
]);

const DEFAULT_AGENT_LABEL = 'Neo';

export function useTraceRunActions({
  applicationId,
  threadId,
  agentName,
  executionId,
  history,
  onAbortSuccess,
  onResumeSuccess,
}: {
  applicationId: string;
  threadId: string;
  agentName: string | undefined;
  executionId: string | undefined;
  history: SmokeTestHistoryResponseDto | undefined;
  onAbortSuccess?: () => Promise<void> | void;
  onResumeSuccess?: () => Promise<void> | void;
}) {
  const [isAborting, setIsAborting] = useState(false);
  const [isRerunning, setIsRerunning] = useState(false);
  const [isResuming, setIsResuming] = useState(false);
  const getAgentLabel = useTraceAgentLabel();
  const agentLabel = getAgentLabel(agentName) ?? DEFAULT_AGENT_LABEL;
  const status = history?.status;
  const signalId = history?.signalId ?? history?.signal?.id;
  const target = useMemo<TraceActionTarget>(
    () => ({
      name: agentName ?? '',
      threadId,
      signalId: signalId ?? null,
      executionId: executionId ?? null,
    }),
    [agentName, executionId, signalId, threadId],
  );
  const isDeepActiveProbe = isDeepActiveProbeTrace(target);

  const canAbort = isDeepActiveProbe
    ? status === 'RUNNING' && Boolean(executionId)
    : Boolean(status) && !NEO_ABORT_BLOCKING_STATUSES.has(status ?? '');
  const canResume = isDeepActiveProbe
    ? status === 'ABORTED' && Boolean(executionId)
    : status === 'ABORTED' || status === 'PAUSED';

  const handleAbort = useCallback(async () => {
    if (!applicationId || isAborting) return;
    setIsAborting(true);

    try {
      await abortTraceExecution({ trace: target, applicationId });
      await onAbortSuccess?.();
    } catch (abortError) {
      console.error('Error aborting trace:', abortError);
      toasterApi.error({
        title: 'Failed to abort',
        description:
          isDeepActiveProbe && abortError instanceof Error && abortError.message
            ? abortError.message
            : 'There was an error aborting the running execution.',
      });
    } finally {
      setIsAborting(false);
    }
  }, [applicationId, isAborting, isDeepActiveProbe, onAbortSuccess, target]);

  const handleRerun = useCallback(
    async ({
      bypassSignalGuardrails = false,
    }: {
      bypassSignalGuardrails?: boolean;
    } = {}) => {
      if (!applicationId || !signalId || isRerunning) return;
      setIsRerunning(true);

      try {
        const authHeaders = await getAuthHeaders();
        const response = await fetch(
          `${getCurrentGatewayUrl()}/ai/agents/neo/run`,
          {
            method: 'POST',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              ...authHeaders,
            },
            body: JSON.stringify({
              signalId,
              applicationId,
              ...(bypassSignalGuardrails && { bypassSignalGuardrails: true }),
            }),
          },
        );

        if (!response.ok) {
          throw new Error('Failed to start Neo');
        }

        const data = (await response.json()) as {
          success: boolean;
          threadId: string | null;
          executionId: string | null;
        };

        if (data.threadId) {
          toasterApi.success({
            title: 'Neo started',
            description: bypassSignalGuardrails
              ? 'Rerunning with signal block overridden.'
              : 'Rerunning Neo.',
          });
        } else {
          toasterApi.info({
            title: 'Neo queued',
            description:
              'Neo could not be started immediately. It may already be running.',
          });
        }
      } catch (rerunError) {
        console.error('Failed to rerun Neo:', rerunError);
        toasterApi.error({
          title: 'Failed to rerun',
          description: 'There was an error starting Neo.',
        });
      } finally {
        setIsRerunning(false);
      }
    },
    [applicationId, signalId, isRerunning],
  );

  const handleResume = useCallback(async () => {
    if (!threadId || !applicationId || isResuming) return;
    setIsResuming(true);

    try {
      await resumeTraceExecution({ trace: target, applicationId });
      await onResumeSuccess?.();
      toasterApi.success({
        title: `${agentLabel} resumed`,
        description: `${agentLabel} is resuming.`,
      });
    } catch (resumeError) {
      console.error(`Failed to resume ${agentLabel}:`, resumeError);
      toasterApi.error({
        title: 'Failed to resume',
        description:
          isDeepActiveProbe &&
          resumeError instanceof Error &&
          resumeError.message
            ? resumeError.message
            : `There was an error resuming ${agentLabel}.`,
      });
    } finally {
      setIsResuming(false);
    }
  }, [
    agentLabel,
    applicationId,
    isDeepActiveProbe,
    isResuming,
    onResumeSuccess,
    target,
    threadId,
  ]);

  return {
    signalId,
    canAbort,
    canResume,
    isAborting,
    isRerunning,
    isResuming,
    handleAbort,
    handleRerun,
    handleResume,
  };
}
